import React, { useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react-lite';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router';
import { HrFlexContainer, HrRed, HrTitleH4, Spinner } from '@components/styles/hr-styles';
import { useToast } from '@/hook/useToast';
import { FormRequest } from '@/vacancy_request/components/form-request';
import { FORM_REQUEST_MODE } from '@/vacancy_request/constants/form-request-mode';
import { usePublicRequestActions } from '@/vacancy_request/hooks/use-public-request-actions';
import {
  getDataForRequests,
  getDataShowRequest,
  getDefaultVacancyRequestTemplate,
  getPublicRequisitionData,
  getVacancyRequestTemplateByHash
} from '@/vacancy_request/services/api';
import storeRequest from '@/vacancy_request/store/storeRequest';

const QUERY_KEYS = {
  internalRequest: 'vacancyRequestInternalData',
  publicRequest: 'vacancyRequestPublicData',
  publicHeader: 'vacancyRequestPublicHeader',
  templateConfig: 'vacancyRequestTemplateConfig'
};

const unavailableMessages = {
  [FORM_REQUEST_MODE.PUBLIC_READ_ONLY]: 'Has completado la revisión de la solicitud de vacante.',
  [FORM_REQUEST_MODE.INTERNAL_READ_ONLY]: 'Has completado la revisión de la solicitud de vacante.',
  [FORM_REQUEST_MODE.EDIT]: 'Has completado la revisión de la solicitud de vacante.',
  [FORM_REQUEST_MODE.TEMPLATE_CREATE]: 'Este enlace ya no está disponible porque la plantilla fue desactivada.'
};

const EmptyState = ({ message }) => (
  <HrFlexContainer
    backgroundColor='#fff'
    justifyContent='center'
    flexDirection='column'
    padding='48px 16px'
    height='100dvh'
  >
    <img src='../../../../assets/img/app/checklist.svg' alt='' height='200' />
    <HrTitleH4 textAlign='center'>{message}</HrTitleH4>
  </HrFlexContainer>
);

EmptyState.propTypes = {
  message: PropTypes.string.isRequired
};

const ApplicationFormPage = ({ aliasAccount, mode, templateLinkUnavailable, editLinkUnavailable }) => {
  const { showToast } = useToast();
  const lastToastErrorKeyRef = useRef(null);
  const { dataViewRequest } = storeRequest();
  const { idRequisition, hash, requisitionHash } = useParams();

  const isPublicMode = mode === FORM_REQUEST_MODE.PUBLIC_READ_ONLY;
  const isTemplateMode = mode === FORM_REQUEST_MODE.TEMPLATE_CREATE;
  const isInternalRequestMode = mode === FORM_REQUEST_MODE.INTERNAL_READ_ONLY || mode === FORM_REQUEST_MODE.EDIT;

  const publicMutations = usePublicRequestActions({ aliasAccount, requisitionHash });

  const internalRequestQuery = useQuery({
    queryKey: [QUERY_KEYS.internalRequest, idRequisition],
    queryFn: () => getDataShowRequest(idRequisition),
    enabled: isInternalRequestMode && Boolean(idRequisition),
    refetchOnWindowFocus: mode !== FORM_REQUEST_MODE.EDIT
  });

  const internalRequestData = internalRequestQuery.data || null;

  // La plantilla que se renderiza queda congelada mientras el formulario está abierto: si se
  // refrescara sola, el usuario vería cambiar labels y obligatoriedades bajo los pies y lo leería
  // como un error. Los cambios se avisan con el diálogo de `useTemplateChangeGuard`, que consulta la
  // versión por su cuenta sin tocar lo que está dibujado.
  const templateConfigQuery = useQuery({
    queryKey: [QUERY_KEYS.templateConfig, aliasAccount, hash],
    queryFn: () => getVacancyRequestTemplateByHash(aliasAccount, hash),
    enabled: isTemplateMode && !templateLinkUnavailable && Boolean(aliasAccount) && Boolean(hash),
    refetchOnWindowFocus: false
  });

  const publicRequestQuery = useQuery({
    queryKey: [QUERY_KEYS.publicRequest, aliasAccount, requisitionHash],
    queryFn: () => getPublicRequisitionData(aliasAccount, requisitionHash),
    enabled: isPublicMode && Boolean(aliasAccount) && Boolean(requisitionHash)
  });

  const publicRequestResponse = publicRequestQuery.data?.data?.response || null;
  const publicRequestIsAvailable = publicRequestResponse?.key === 'success';
  const publicRequestData = publicRequestIsAvailable ? publicRequestResponse.data : null;

  const publicHeaderQuery = useQuery({
    queryKey: [QUERY_KEYS.publicHeader, aliasAccount],
    queryFn: () => getDataForRequests(aliasAccount),
    enabled: isPublicMode && Boolean(aliasAccount) && publicRequestIsAvailable
  });

  const publicTemplateConfigQuery = useQuery({
    queryKey: [QUERY_KEYS.templateConfig, aliasAccount, publicRequestData?.templateHash],
    queryFn: () => getVacancyRequestTemplateByHash(aliasAccount, publicRequestData.templateHash),
    enabled: isPublicMode && Boolean(aliasAccount) && Boolean(publicRequestData?.templateHash),
    refetchOnWindowFocus: false
  });

  const internalTemplateConfigQuery = useQuery({
    queryKey: [QUERY_KEYS.templateConfig, aliasAccount, internalRequestData?.templateHash],
    queryFn: () => getVacancyRequestTemplateByHash(aliasAccount, internalRequestData.templateHash),
    enabled: isInternalRequestMode && Boolean(aliasAccount) && Boolean(internalRequestData?.templateHash),
    refetchOnWindowFocus: false
  });

  const internalRequestLoaded = isInternalRequestMode && Boolean(internalRequestData);
  const internalRequestHasNoTemplate = internalRequestLoaded && !internalRequestData?.templateHash;
  const internalDefaultTemplateQuery = useQuery({
    queryKey: [QUERY_KEYS.templateConfig, aliasAccount, 'default'],
    queryFn: () => getDefaultVacancyRequestTemplate(aliasAccount),
    enabled: internalRequestHasNoTemplate && Boolean(aliasAccount),
    refetchOnWindowFocus: false
  });

  const isLoading = isPublicMode
    ? publicRequestQuery.isLoading || (publicRequestIsAvailable && (publicHeaderQuery.isLoading || publicTemplateConfigQuery.isLoading))
    : (
      isTemplateMode
        ? templateConfigQuery.isLoading
        : internalRequestQuery.isLoading ||
        (Boolean(internalRequestData?.templateHash) && internalTemplateConfigQuery.isLoading) ||
        (internalRequestHasNoTemplate && internalDefaultTemplateQuery.isLoading)
    );

  const hasError = isPublicMode
    ? publicRequestQuery.isError || (publicRequestIsAvailable && (publicHeaderQuery.isError || publicTemplateConfigQuery.isError))
    : (
      isTemplateMode
        ? templateConfigQuery.isError
        : internalRequestQuery.isError || internalTemplateConfigQuery.isError
    );

  const formData = isPublicMode ? publicRequestData : internalRequestData || {};
  const templateConfig = isPublicMode
    ? publicTemplateConfigQuery.data || null
    : (isInternalRequestMode
      ? internalTemplateConfigQuery.data || internalDefaultTemplateQuery.data || null
      : templateConfigQuery.data || null
    );
  const topNameAccount = isPublicMode ? publicHeaderQuery.data?.nameAccount : dataViewRequest.nameAccount;
  const topImage = isPublicMode ? publicHeaderQuery.data?.imagePhoto : dataViewRequest.imagePhoto;

  const shouldShowUnavailableState = useMemo(() => {
    if (templateLinkUnavailable && isTemplateMode) return true;
    if (editLinkUnavailable && mode === FORM_REQUEST_MODE.EDIT) return true;
    if (isPublicMode) return !publicRequestIsAvailable;
    if (isInternalRequestMode) return internalRequestQuery.isError;
    if (isTemplateMode) return templateConfigQuery.isError || !templateConfig;
    return false;
  }, [
    internalRequestQuery.isError,
    isInternalRequestMode,
    isPublicMode,
    publicRequestIsAvailable,
    isTemplateMode,
    templateConfig,
    templateConfigQuery.isError,
    templateLinkUnavailable,
    editLinkUnavailable,
    mode
  ]);

  useEffect(() => {
    if (!hasError || shouldShowUnavailableState) {
      lastToastErrorKeyRef.current = null;
      return;
    }

    const errorKey = `${mode}:${idRequisition || ''}:${hash || ''}:${requisitionHash || ''}`;
    if (lastToastErrorKeyRef.current === errorKey) return;
    lastToastErrorKeyRef.current = errorKey;

    showToast({
      color: HrRed,
      iconClassName: 'hr-Cross',
      message: 'Hubo un error inesperado, vuelve a intentarlo'
    });
  }, [hasError, shouldShowUnavailableState, showToast, mode, idRequisition, hash, requisitionHash]);

  if (isLoading) {
    return (
      <HrFlexContainer justifyContent='center'>
        <Spinner className='hr-Charge' category='tertiaryGreen' />
      </HrFlexContainer>
    );
  }

  if (shouldShowUnavailableState) {
    return (
      <EmptyState
        message={unavailableMessages[mode]}
      />
    );
  }

  return (
    <FormRequest
      aliasAccount={aliasAccount}
      dataForm={formData}
      idRequisition={idRequisition}
      mode={mode}
      templateConfig={templateConfig}
      publicMutations={isPublicMode ? publicMutations : null}
    />
  );
};

ApplicationFormPage.propTypes = {
  aliasAccount: PropTypes.string.isRequired,
  mode: PropTypes.oneOf(Object.values(FORM_REQUEST_MODE)).isRequired,
  templateLinkUnavailable: PropTypes.bool,
  editLinkUnavailable: PropTypes.bool
};

ApplicationFormPage.defaultProps = {
  templateLinkUnavailable: false,
  editLinkUnavailable: false
};

export default observer(ApplicationFormPage);
